import { Box, Button, Center, Flex, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useCallback, useState } from 'react';
import { useJwt } from 'react-jwt';

import { useLEAccordionItemContext } from '../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import LogoutConfirmModal from '../../../components/LogoutConfirmModal';
import TextWithTooltip from '../../../components/TextWithTooltip';
import { Input, Label } from '../../../components/formComponents';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import { getTempInfoCountryConfig } from '../../../constants/countries';
import { LDFlags } from '../../../constants/flags';
import {
  useGetMethodFiSessionStateLazyQuery,
  useLikelyAddressLazyQuery,
  useRetrieveEntityIdentitiesMutation,
  useTemporaryInfoUpdateMutation,
} from '../../../gql/generated/graphql';
import { useFlag, useTempInfo } from '../../../hooks';
import { CookieKeys, useCookie } from '../../../hooks/useCookie';
import useOptOutLookup from '../../../hooks/useOptOutLookup';
import { trackEnhancedLead } from '../../../services/analytics/conversionEvents';
import { logger } from '../../../services/sentry';
import { JWTToken, getEmailFromJWT } from '../../../utils/auth';
import { queryWithTimeout } from '../../../utils/queries';
import {
  VerifyPersonalInfoValues,
  verifyPersonalInfoInitialValues,
  verifyPersonalInfoValidationSchema,
} from '../utils';

const TOOLTIP_LABEL =
  'Providing this information helps us 1) fulfill lender requirements ' +
  'to verify your identity and 2) contact you about your transaction & application status.';

type VerifyPersonalInfoProps = {
  showWhyTooltip?: boolean;
};

const VerifyPersonalInfo = ({ showWhyTooltip = true }: VerifyPersonalInfoProps) => {
  const [accessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const { decodedToken } = useJwt<JWTToken>(accessToken || '');
  const accessTokenEmail = getEmailFromJWT(decodedToken);
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const checkOptOut = useOptOutLookup();
  const methodFiseparateIdentityEnabled = useFlag(LDFlags.METHODFI_SEPARATE_IDENTITY_CALL);

  const { updateState, isExpanded, setOpenAccordionIndex, index } = useLEAccordionItemContext();
  const { info } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const addressAutofillEnabled =
    useFlag(LDFlags.COM_PRIMARY_ADDRESS_AUTOFILL) &&
    getTempInfoCountryConfig(info?.data).addressAutofill;
  const [logoutConfirmModalOpen, setLogoutConfirmModalOpen] = useState(false);

  const [retrieveEntityIdentities] = useRetrieveEntityIdentitiesMutation();

  const [getMethodFiSessionState] = useGetMethodFiSessionStateLazyQuery();
  const [getLikelyAddress] = useLikelyAddressLazyQuery();

  const fetchMethodFiIdentities = async () => {
    const methodFiSessionResponse = await getMethodFiSessionState({ fetchPolicy: 'network-only' });
    const methodFiSession = methodFiSessionResponse?.data?.getMethodFiSessionState;

    const entityId = methodFiSession?.entityId;
    const savedIdentities = methodFiSession?.identities;
    const phoneVerified = methodFiSession?.entity?.verification?.phone?.verified ?? false;

    if (phoneVerified && entityId && !savedIdentities?.length) {
      try {
        await retrieveEntityIdentities({ variables: { entityId } });
      } catch (e) {
        logger.error('VerifyPersonalInfo', 'fetchMethodFiIdentities failed', e, null);
      }
    }
  };

  const tempInfoData = info?.data;
  const methodFiEnabled = !!tempInfoData?.is_methodfi_flow;
  const loggedIn = !!accessToken;

  const handlePhoneBlur = useCallback(
    async (phoneNumber: string) => {
      if (addressAutofillEnabled && phoneNumber && !tempInfoData?.address_line) {
        queryWithTimeout(() => getLikelyAddress({ variables: { phoneNumber } }));
      }
    },
    [addressAutofillEnabled, tempInfoData?.address_line, getLikelyAddress],
  );

  const onSubmitForm = async (values: VerifyPersonalInfoValues) => {
    updateState({ status: 'loading' });

    const data = {
      first_name: values.first_name.trim(),
      middle_name: values.middle_name.trim(),
      last_name: values.last_name.trim(),
      email: values.email,
      phone_number: values.phone_number,
    };

    await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data,
        },
      },
    });

    if (methodFiEnabled && methodFiseparateIdentityEnabled) {
      await fetchMethodFiIdentities();
    }

    await checkOptOut({ email: data.email, phone: data.phone_number });
    trackEnhancedLead({
      tempInfoId: info?.id,
      email: data.email,
      phone_number: data.phone_number,
      first_name: data.first_name,
      last_name: data.last_name,
    });

    updateState({ status: 'complete' });
    setOpenAccordionIndex(index + 1);
  };

  return (
    <>
      <Box>
        <Text textAlign="center" mb={4}>
          Please verify your contact information
        </Text>
        {showWhyTooltip && (
          <Center mb={{ base: 4, md: 0 }}>
            <TextWithTooltip label={TOOLTIP_LABEL}>Why do we need this?</TextWithTooltip>
          </Center>
        )}
        <Formik
          enableReinitialize
          initialValues={verifyPersonalInfoInitialValues(tempInfoData, accessTokenEmail)}
          validationSchema={verifyPersonalInfoValidationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={onSubmitForm}
        >
          {({ isSubmitting, errors, values }) => {
            const hasErrors = Object.values(errors).some((value) => value !== '');
            return (
              <Form>
                <Input
                  label="Phone Number"
                  name="phone_number"
                  mask="(999) 999-9999"
                  _container={{ mb: 2 }}
                  placeholder="Phone Number"
                  type="tel"
                  autoFocus
                  onBlur={() => handlePhoneBlur(values.phone_number)}
                />
                <Label>NAME</Label>
                <Flex
                  mb={2}
                  w="100%"
                  flexDir={{ base: 'column', md: 'row' }}
                  justifyContent="space-between"
                >
                  <Input name="first_name" placeholder="First" _container={{ mr: { md: 1 } }} />
                  <Input
                    name="middle_name"
                    placeholder="Middle"
                    _container={{ mx: { md: 1 }, my: { base: 2, md: 0 } }}
                  />
                  <Input name="last_name" placeholder="Last" _container={{ ml: { md: 1 } }} />
                </Flex>
                <Input
                  name="email"
                  placeholder="Email"
                  label="EMAIL ADDRESS"
                  isDisabled={loggedIn}
                  labelProps={{ opacity: '1 !important' }}
                  helperText={
                    loggedIn && (
                      <>
                        Not you?{' '}
                        <Button
                          fontSize="sm"
                          variant="link"
                          onClick={() => setLogoutConfirmModalOpen(true)}
                        >
                          Log out here
                        </Button>
                      </>
                    )
                  }
                />
                <Center>
                  <PrimaryButton
                    mt="30px"
                    mb={{
                      base: '20px',
                      lg: '0',
                    }}
                    loading={isSubmitting}
                    type="submit"
                  />
                </Center>
                {hasErrors && (
                  <Text color="leaseEndRed" textAlign="center" mt={{ base: 0, lg: 4 }}>
                    Review fields above for errors.
                  </Text>
                )}
              </Form>
            );
          }}
        </Formik>
      </Box>
      <LogoutConfirmModal
        isOpen={logoutConfirmModalOpen}
        handleClose={() => setLogoutConfirmModalOpen(false)}
      />
    </>
  );
};

export default VerifyPersonalInfo;
